import { useRouter } from 'next/router';

import { Box, FieldSet, Label, LabeledControl, Legend, Paragraph, Radio } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditWebApiHandler_hideStreetAddress } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFormMode } from '../../../contexts/FormModeContext';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import propertyUtils from '../../../utils/propertyUtils';

const inputName = 'hideStreetAddress';

const HideAddressFormField = ({ isRelistingFieldset }: { isRelistingFieldset?: boolean }) => {
  const router = useRouter();
  const formMode = useFormMode();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const initialHideAddress = listingDetails?.hideStreetAddress ?? false;

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hideStreetAddress,
  });

  const hideAddressField = useFieldSave({
    fieldName: inputName,
    initialValue: String(initialHideAddress),
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      getParams: { listingAlias, hideStreetAddress: value === 'true' },
    }),
    autoSaveOnChange: true,
  });

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
    unit: listingDetails?.unit,
  });

  return (
    <div data-testid="HideAddressFormField">
      <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
        {formattedCompleteAddress}
      </Paragraph>

      <FieldSet
        legend={<Legend size={isRelistingFieldset ? 'lg' : 'md'}>Hide property address</Legend>}
        error={!!hideAddressField.error}
        description={hideAddressField.error}
      >
        <Box css={{ display: 'flex', paddingLeft: '200', flexDirection: 'column' }}>
          <LabeledControl
            label={
              <Label>
                No&nbsp;
                <strong>(Recommended)</strong>
              </Label>
            }
            control={
              <Radio
                name={inputName}
                value="false"
                checked={hideAddressField.value === 'false'}
                onChange={(e) => hideAddressField.onChange(e.target.value)}
              />
            }
          />

          <LabeledControl
            label={<Label>Yes</Label>}
            control={
              <Radio
                name={inputName}
                value="true"
                checked={hideAddressField.value === 'true'}
                onChange={(e) => hideAddressField.onChange(e.target.value)}
              />
            }
          />
        </Box>
      </FieldSet>

      {formMode.mode === 'modal' && (
        <Box css={{ marginTop: '800' }}>
          <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '200' }}>
            Hiding the property address
          </Paragraph>
          <Paragraph>
            You can hide the address if privacy is a concern, but the listing may receive fewer views and contacts than
            listings that show the property address.
          </Paragraph>
        </Box>
      )}
    </div>
  );
};

export default HideAddressFormField;
